import { useEffect, useRef, useState } from 'react';

const LEAFLET_CSS_ID = 'mynazca-leaflet-css';
const LEAFLET_JS_ID = 'mynazca-leaflet-js';

declare global {
  interface Window {
    L?: any;
  }
}

async function loadLeaflet(): Promise<void> {
  if (typeof document === 'undefined') return;
  if (window.L) return;

  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const link = document.createElement('link');
    link.id = LEAFLET_CSS_ID;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  const existing = document.getElementById(LEAFLET_JS_ID) as HTMLScriptElement | null;
  if (existing) {
    await new Promise<void>((resolve, reject) => {
      if (window.L) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Leaflet load failed')), { once: true });
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = LEAFLET_JS_ID;
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Leaflet load failed')), { once: true });
    document.head.appendChild(script);
  });
}

export type MapCenter = { lat: number; lng: number };

/** [lat, lng][] — FIT 合成後のルートを地図に重ねるときに渡す */
export type MapRouteLatLngs = [number, number][];

type MyNazcaMapProps = {
  center: MapCenter;
  onCenterChange: (c: MapCenter) => void;
  disabled?: boolean;
  /** FIT 生成後の appliedPoints を lat/lng の折れ線で表示（2点未満は描画しない） */
  routeLatLngs?: MapRouteLatLngs | null;
};

export function MyNazcaMap({ center, onCenterChange, disabled, routeLatLngs }: MyNazcaMapProps) {
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const polylineRef = useRef<any>(null);
  const onCenterChangeRef = useRef(onCenterChange);
  onCenterChangeRef.current = onCenterChange;
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const el = mapElRef.current;
    if (!el) return;

    void (async () => {
      try {
        await loadLeaflet();
        if (cancelled || !mapElRef.current || !window.L) return;
        const L = window.L;
        const map = L.map(mapElRef.current).setView([center.lat, center.lng], 13);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);
        const marker = L.marker([center.lat, center.lng], { draggable: !disabled }).addTo(map);
        marker.on('dragend', () => {
          const ll = marker.getLatLng();
          onCenterChangeRef.current({ lat: ll.lat, lng: ll.lng });
        });
        map.on('click', (e: any) => {
          if (disabled) return;
          const { lat, lng } = e.latlng;
          marker.setLatLng([lat, lng]);
          onCenterChangeRef.current({ lat, lng });
        });
        mapInstanceRef.current = map;
        markerRef.current = marker;
        setMapReady(true);
      } catch {
        // 親でエラー表示しない（地図は補助）
      }
    })();

    return () => {
      cancelled = true;
      setMapReady(false);
      try {
        mapInstanceRef.current?.remove();
      } catch {
        // noop
      }
      mapInstanceRef.current = null;
      markerRef.current = null;
      polylineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウントのみ地図生成
  }, []);

  useEffect(() => {
    const marker = markerRef.current;
    const map = mapInstanceRef.current;
    if (!marker || !map) return;
    if (disabled) {
      marker.dragging?.disable?.();
    } else {
      marker.dragging?.enable?.();
    }
  }, [disabled]);

  useEffect(() => {
    const marker = markerRef.current;
    const map = mapInstanceRef.current;
    if (!marker || !map) return;
    const m = marker.getLatLng();
    if (Math.abs(m.lat - center.lat) < 1e-7 && Math.abs(m.lng - center.lng) < 1e-7) return;
    marker.setLatLng([center.lat, center.lng]);
    map.setView([center.lat, center.lng], map.getZoom(), { animate: false });
  }, [center.lat, center.lng]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const L = window.L;
    const marker = markerRef.current;
    if (!mapReady || !map || !L) return;

    try {
      if (polylineRef.current) {
        map.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }
    } catch {
      // noop
    }

    const line = routeLatLngs?.filter((p) => p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])) ?? [];
    if (line.length < 2) return;

    try {
      const latLngs = line as [number, number][];
      const pl = L.polyline(latLngs, {
        color: '#2563eb',
        weight: 4,
        opacity: 0.92,
        lineJoin: 'round',
        lineCap: 'round',
      }).addTo(map);
      polylineRef.current = pl;
      const bounds = L.latLngBounds(latLngs);
      if (marker) {
        bounds.extend(marker.getLatLng());
      }
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [28, 28], maxZoom: 16 });
      }
    } catch {
      // noop
    }
  }, [mapReady, routeLatLngs]);

  return <div ref={mapElRef} className="mynazca-app__map" role="application" aria-label="Map" />;
}
